import { ImportButton } from "@/components/ImportButton";

import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Download, Upload, Plus, Trash2, PartyPopper, CalendarRange } from "lucide-react";
import { HotelizerRevenue } from "@/components/HotelizerRevenue";
import { IncomeTargets } from "@/components/IncomeTargets";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Income() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [year, setYear] = useState(2026);
  const [form, setForm] = useState({
    kind: "income", date: todayStr(), party: "", doc_no: "", description: "",
    account_code: "4300", cost_center: "TERA", net: 0, vat: 0, payment_date: "",
    payment_method: "Τράπεζα / Έμβασμα", nights: 0,
  });

  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const { data: events = [] } = useQuery({
    queryKey: ["events", year],
    queryFn: async () => (await api.get(`/finance/events?year=${year}`)).data,
  });
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["entries", year, "income"],
    queryFn: async () => (await api.get(`/finance/entries?year=${year}&kind=income`)).data.filter((e) => !(e.source || "").startsWith("hotelizer")),
  });
  const { data: bookings = [] } = useQuery({
    queryKey: ["bookings", year],
    queryFn: async () => (await api.get(`/finance/bookings?year=${year}`)).data,
  });
  const { data: vatSum } = useQuery({
    queryKey: ["bookings-vat", year],
    queryFn: async () => (await api.get(`/finance/bookings-vat-summary?year=${year}`)).data,
  });

  const refresh = () => ["entries", "bookings", "bookings-vat", "cashflow", "pl-report", "pl-kpi"].forEach((k) =>
    qc.invalidateQueries({ queryKey: [k] }));

  const create = useMutation({
    mutationFn: async () => (await api.post("/finance/entries", {
      ...form, kind: "income", net: Number(form.net) || 0, vat: Number(form.vat) || 0,
      nights: Number(form.nights) || 0, payment_date: form.payment_date || null,
    })).data,
    onSuccess: () => { refresh(); toast.success("Το αναμενόμενο έσοδο καταχωρήθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/entries/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const delBooking = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/bookings/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Η κράτηση διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const upload = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/finance/import/cashflow/${commit ? "commit" : "preview"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d, commit) => {
      if (!commit) {
        setPreview(d);
        toast.info(`${d.valid_bookings} έγκυρες κρατήσεις · ${d.invalid} με λάθη`);
        return;
      }
      setPreview(null); setFile(null); if (fileRef.current) fileRef.current.value = "";
      refresh();
      toast.success(`Εισήχθησαν ${d.bookings_imported} κρατήσεις`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";
  const bookingsTotal = bookings.reduce((s, b) => s + (b.net_value || 0), 0);
  const isImport = (e) => (e.doc_no || "").startsWith("MS-");
  const eventEntries = entries.filter((e) => e.account_code === "4300");
  const eventsTotal = eventEntries.reduce((s, e) => s + e.net, 0) + events.reduce((s, ev) => s + (ev.net || 0), 0);
  const unpaid = entries.filter((e) => !isImport(e)).reduce((s, e) => s + (e.payment_date ? 0 : e.total), 0)
    + events.reduce((s, ev) => s + Math.max(ev.balance || 0, 0), 0);

  return (
    <div className="space-y-6" data-testid="income-page">
      <div className="flex justify-end"><ImportButton only={["bookings"]} label="Ανέβασμα κρατήσεων (Excel)" /></div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Έσοδα</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Κρατήσεις & Εκδηλώσεις</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Ανεβάστε τις κρατήσεις από Excel και καταχωρήστε χειροκίνητα εκδηλώσεις & γάμους ως αναμενόμενα έσοδα.</p>
        </div>
        <div>
          <label className={lab}>Έτος</label>
          <select data-testid="inc-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="mt-1 block rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#45423C]">
            {[2025, 2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <HotelizerRevenue year={year} canEdit={canEdit} />

      <IncomeTargets year={year} canEdit={canEdit} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Κρατήσεις", bookings.length], ["Καθαρά κρατήσεων (χωρίς ΦΠΑ)", fmtEuro(bookingsTotal)],
          ["Εκδηλώσεις", fmtEuro(eventsTotal)], ["Ανείσπρακτα", fmtEuro(unpaid)]].map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`inc-kpi-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{v}</div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="inc-upload-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="font-medium text-[#45423C] flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-[#C86235]" /> Εβδομαδιαίο Excel κρατήσεων
              </h2>
              <p className="text-sm text-[#6F6A60] mt-1">Direct NR: 100% στην κράτηση · Direct Flex: 50%/50% · OTA: με την αναχώρηση μείον προμήθεια.</p>
            </div>
            <button data-testid="inc-template" onClick={() => downloadXlsx("/finance/cashflow-template.xlsx?only=bookings", "TAF_Template_Kratiseis.xlsx")}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
              <Download className="h-4 w-4" /> Πρότυπο Excel
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t border-[#E3DED4] pt-4">
            <input data-testid="inc-file" ref={fileRef} type="file" accept=".xlsx" className="text-sm"
              onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
            <button data-testid="inc-preview-btn" onClick={() => upload.mutate(false)} disabled={!file || upload.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-40">
              <Upload className="h-4 w-4" /> Προεπισκόπηση
            </button>
            {preview && (
              <button data-testid="inc-commit-btn" onClick={() => upload.mutate(true)} disabled={upload.isPending || !preview.valid_bookings}
                className="rounded-xl bg-[#6E7A63] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                Εισαγωγή {preview.valid_bookings} κρατήσεων ({fmtEuro(preview.total_bookings_value)})
              </button>
            )}
          </div>
          {preview && (
            <div className="border border-[#E3DED4] rounded-xl overflow-hidden text-xs" data-testid="inc-preview">
              {(preview.bookings || []).map((b) => (
                <div key={b.row} data-testid={`inc-prev-${b.row}`}
                  className={`px-3 py-2 border-b border-[#E3DED4] last:border-0 flex justify-between gap-3 ${b.errors.length ? "bg-[#FEF2F2]" : ""}`}>
                  <span>{b.guest || "—"} · {b.unit || "—"} · {b.checkin} → {b.checkout}</span>
                  <span className="tabular">{fmtEuro(b.gross)}{b.errors.length ? <em className="text-[#991B1B] ml-2">{b.errors.join(", ")}</em> : null}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="inc-form">
          <div className="flex items-center gap-2 mb-4">
            <PartyPopper className="h-4 w-4 text-[#C86235]" />
            <h2 className="font-medium text-[#45423C]">Νέα εκδήλωση / γάμος — αναμενόμενο έσοδο</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <label className={lab}>Ημερομηνία</label>
              <input data-testid="inc-date" type="date" className={inp} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className={lab}>Πελάτης / Ζευγάρι</label>
              <input data-testid="inc-party" className={inp} value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className={lab}>Κατηγορία εσόδου</label>
              <select data-testid="inc-account" className={inp} value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })}>
                {(meta?.accounts || []).filter((a) => a.section === "revenue")
                  .map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lab}>Cost Center</label>
              <select data-testid="inc-cc" className={inp} value={form.cost_center} onChange={(e) => setForm({ ...form, cost_center: e.target.value })}>
                {(meta?.cost_centers || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className={lab}>Περιγραφή</label>
              <input data-testid="inc-desc" className={inp} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Γάμος 120 άτομα, TERA" />
            </div>
            <div>
              <label className={lab}>Καθαρή Αξία (€)</label>
              <input data-testid="inc-net" type="number" className={inp} value={form.net} onChange={(e) => setForm({ ...form, net: e.target.value })} />
            </div>
            <div>
              <label className={lab}>ΦΠΑ (€)</label>
              <input data-testid="inc-vat" type="number" className={inp} value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Αναμ. Είσπραξη</label>
              <input data-testid="inc-paydate" type="date" className={inp} value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Τρόπος</label>
              <select data-testid="inc-method" className={inp} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                {(meta?.payment_methods || []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button data-testid="inc-save" onClick={() => create.mutate()} disabled={create.isPending}
                className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Plus className="h-4 w-4" /> Καταχώρηση
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4]">
          <h2 className="font-medium text-[#45423C]">Κρατήσεις {year}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Πελάτης</th><th className="px-4 py-3">Πηγή</th><th className="px-4 py-3">Μονάδα</th>
                <th className="px-4 py-3">Check-in</th><th className="px-4 py-3">Check-out</th>
                <th className="px-4 py-3 text-center">Νύχτες</th><th className="px-4 py-3 text-right">Ποσό (μικτό)</th>
                <th className="px-4 py-3 text-right">Καθαρή (χωρίς ΦΠΑ)</th>
                <th className="px-4 py-3 text-right">Προμήθεια</th><th className="px-4 py-3">Εισπράξεις</th>
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`bk-row-${b.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2 font-medium text-[#45423C]">{b.guest || "—"}</td>
                  <td className="px-4 py-2 text-xs text-[#6F6A60]">{b.source === "ota" ? (b.ota_name || "OTA") : b.source === "direct_nr" ? "Direct NR" : "Direct Flex"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{b.unit || "—"}</td>
                  <td className="px-4 py-2 tabular text-xs">{fmtDate(b.checkin)}</td>
                  <td className="px-4 py-2 tabular text-xs">{fmtDate(b.checkout)}</td>
                  <td className="px-4 py-2 text-center tabular">{b.nights}</td>
                  <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(b.gross)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6E7A63]">{fmtEuro(b.net_value)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(b.commission)}</td>
                  <td className="px-4 py-2 text-xs text-[#6F6A60]">
                    {(b.receipts || []).map((rc, i) => <div key={`${rc.date}-${rc.amount}-${i}`}>{fmtDate(rc.date)} · {fmtEuro(rc.amount)}</div>)}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right">
                      <button data-testid={`bk-delete-${b.id}`} onClick={() => delBooking.mutate(b.id)}
                        className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-[#6F6A60]">
                  Καμία κράτηση — ανεβάστε το εβδομαδιαίο Excel παραπάνω.
                </td></tr>
              )}
            </tbody>
            {bookings.length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white font-semibold" data-testid="bookings-totals">
                  <td className="px-4 py-2.5" colSpan={5}>ΣΥΝΟΛΟ {bookings.length} κρατήσεων</td>
                  <td className="px-4 py-2.5 text-center tabular">{bookings.reduce((s, b) => s + (b.nights || 0), 0)}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(bookings.reduce((s, b) => s + (b.gross || 0), 0))}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(bookings.reduce((s, b) => s + (b.net_value || 0), 0))}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(bookings.reduce((s, b) => s + (b.commission || 0), 0))}</td>
                  <td className="px-4 py-2.5 text-right tabular">
                    {fmtEuro(bookings.reduce((s, b) => s + (b.receipts || []).reduce((t, rc) => t + (rc.amount || 0), 0), 0))}
                  </td>
                  {canEdit && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {vatSum && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="bookings-vat-summary">
          <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-medium text-[#45423C]">Σύνοψη ΦΠΑ κρατήσεων {year}</h2>
            <span className="text-xs text-[#6F6A60]">Ανά μήνα check-in · για διασταύρωση με τη δήλωση ΦΠΑ</span>
          </div>
          <div className="grid lg:grid-cols-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                    <th className="px-4 py-2.5">Μήνας</th><th className="px-4 py-2.5 text-right">Μικτά</th>
                    <th className="px-4 py-2.5 text-right">Καθαρά</th><th className="px-4 py-2.5 text-right">ΦΠΑ</th>
                  </tr>
                </thead>
                <tbody>
                  {vatSum.months.filter((m) => m.gross).map((m) => (
                    <tr key={m.month} className="border-t border-[#E3DED4]">
                      <td className="px-4 py-2">{m.label}</td>
                      <td className="px-4 py-2 text-right tabular">{fmtEuro(m.gross)}</td>
                      <td className="px-4 py-2 text-right tabular text-[#6E7A63]">{fmtEuro(m.net)}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(m.vat)}</td>
                    </tr>
                  ))}
                  {vatSum.months.every((m) => !m.gross) && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-[#6F6A60]">Καμία κράτηση</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-[#45423C] text-white font-semibold" data-testid="vat-sum-totals">
                    <td className="px-4 py-2.5">ΣΥΝΟΛΟ</td>
                    <td className="px-4 py-2.5 text-right tabular">{fmtEuro(vatSum.totals.gross)}</td>
                    <td className="px-4 py-2.5 text-right tabular">{fmtEuro(vatSum.totals.net)}</td>
                    <td className="px-4 py-2.5 text-right tabular">{fmtEuro(vatSum.totals.vat)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="overflow-x-auto border-t lg:border-t-0 lg:border-l border-[#E3DED4]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                    <th className="px-4 py-2.5">Τύπος εσόδου</th><th className="px-4 py-2.5 text-center">ΦΠΑ%</th>
                    <th className="px-4 py-2.5 text-right">Μικτά</th><th className="px-4 py-2.5 text-right">ΦΠΑ</th>
                  </tr>
                </thead>
                <tbody>
                  {vatSum.by_type.map((t) => (
                    <tr key={t.type} className="border-t border-[#E3DED4]">
                      <td className="px-4 py-2">{t.label}</td>
                      <td className="px-4 py-2 text-center tabular text-[#6F6A60]">{t.rate}%</td>
                      <td className="px-4 py-2 text-right tabular">{fmtEuro(t.gross)}</td>
                      <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(t.vat)}</td>
                    </tr>
                  ))}
                  {vatSum.by_type.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-[#6F6A60]">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4]">
          <h2 className="font-medium text-[#45423C]">Εκδηλώσεις & λοιπά έσοδα {year}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Ημ/νία</th>
                <th className="px-4 py-3">Πελάτης</th>
                <th className="px-4 py-3">Περιγραφή</th>
                <th className="px-4 py-3">Κατηγορία</th>
                <th className="px-4 py-3">CC</th>
                <th className="px-4 py-3 text-right">Καθαρή</th>
                <th className="px-4 py-3 text-right">ΦΠΑ</th>
                <th className="px-4 py-3 text-right">Σύνολο</th>
                <th className="px-4 py-3">Είσπραξη</th>
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {entries.map((r) => (
                <tr key={r.id} data-testid={`inc-row-${r.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2 tabular">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2 font-medium text-[#45423C]">{r.party || "—"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.description || "—"}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{r.account_code} · {r.account_name}</td>
                  <td className="px-4 py-2">{r.cost_center}</td>
                  <td className="px-4 py-2 text-right tabular">{fmtEuro(r.net)}</td>
                  <td className="px-4 py-2 text-right tabular text-[#6F6A60]">{fmtEuro(r.vat)}</td>
                  <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(r.total)}</td>
                  <td className="px-4 py-2 text-xs">
                    {r.payment_date ? <span className="text-[#6E7A63]">{fmtDate(r.payment_date)}</span>
                      : <span className="text-[#991B1B]">Εκκρεμεί</span>}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right">
                      <button data-testid={`inc-delete-${r.id}`} onClick={() => remove.mutate(r.id)}
                        className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {!isLoading && entries.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">
                  Καμία εκδήλωση — καταχωρήστε γάμο ή event για να μπει στην πρόβλεψη εσόδων.
                </td></tr>
              )}
            </tbody>
            {!isLoading && entries.length > 0 && (
              <tfoot>
                <tr className="bg-[#45423C] text-white font-semibold" data-testid="income-entries-totals">
                  <td className="px-4 py-2.5" colSpan={5}>ΣΥΝΟΛΟ {entries.length} εγγραφών</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(entries.reduce((s, r) => s + (r.net || 0), 0))}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(entries.reduce((s, r) => s + (r.vat || 0), 0))}</td>
                  <td className="px-4 py-2.5 text-right tabular">{fmtEuro(entries.reduce((s, r) => s + (r.total || 0), 0))}</td>
                  <td />
                  {canEdit && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
